<template>
  <div ref="chartContainer" class="donut-chart-container" :class="{ 'donut-chart-mini': !showLegend }">
    <div v-if="data.length === 0" class="no-data">
      <slot name="no-data">No data available</slot>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatNum } from '@/util/number';

export default {
  name: 'DonutChart',
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    labelKey: {
      type: String,
      default: 'name',
    },
    valueKey: {
      type: String,
      default: 'value',
    },
    colors: {
      type: Array,
      default: () => ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe', '#43e97b', '#38f9d7'],
    },
    width: {
      type: Number,
      default: 180,
    },
    height: {
      type: Number,
      default: 200,
    },
    innerRadiusRatio: {
      type: Number,
      default: 0.5,
    },
    valueFormatter: {
      type: Function,
      default: null,
    },
    // false = legend dirender di luar (HTML)
    showLegend: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      chart: null,
    };
  },
  watch: {
    data: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
    colors: {
      handler() {
        this.drawChart();
      },
    },
    showLegend() {
      this.drawChart();
    },
  },
  mounted() {
    this.drawChart();
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    drawChart() {
      if (!this.$refs.chartContainer || this.data.length === 0) return;

      // Clear existing chart
      d3.select(this.$refs.chartContainer).selectAll('*').remove();

      const margin = this.showLegend ? 10 : 4;
      const radius = this.showLegend
        ? Math.min(this.width, this.height - 50) / 2 - margin
        : Math.min(this.width, this.height) / 2 - margin;
      // Baris legend nambah, SVG ikut tinggi tapi donut tetap di posisi semula
      const svgHeight = this.showLegend
        ? Math.max(this.height, radius * 2 + 40 + this.data.length * 18)
        : this.height;
      const centerY = this.showLegend ? this.height / 2 - 20 : this.height / 2;

      // Color scale
      const color = d3.scaleOrdinal()
        .domain(this.data.map((d) => d[this.labelKey]))
        .range(this.colors);

      // Create SVG
      // lawan svg{overflow:hidden} bootstrap
      const svg = d3.select(this.$refs.chartContainer)
        .append('svg')
        .attr('width', this.width)
        .attr('height', svgHeight)
        .attr('viewBox', `0 0 ${this.width} ${svgHeight}`)
        .style('overflow', 'visible')
        .append('g')
        .attr('transform', `translate(${this.width / 2},${centerY})`);

      // Create pie layout
      const pie = d3.pie().value((d) => d[this.valueKey]).sort(null);
      const arc = d3.arc().innerRadius(radius * this.innerRadiusRatio).outerRadius(radius);
      const arcHover = d3.arc().innerRadius(radius * this.innerRadiusRatio).outerRadius(radius * 1.1);

      // Draw arcs
      const slices = svg.selectAll('.arc')
        .data(pie(this.data))
        .enter()
        .append('g')
        .attr('class', 'arc');

      slices.append('path')
        .attr('d', arc)
        .attr('fill', (d) => color(d.data[this.labelKey]))
        .attr('stroke', '#fff')
        .attr('stroke-width', '2px')
        .style('cursor', 'pointer')
        .on('mouseover', (event, d) => {
          d3.select(event.currentTarget)
            .transition()
            .duration(200)
            .attr('d', arcHover);

          if (this.showLegend) {
            // Show tooltip
            svg.append('text')
              .attr('class', 'donut-hover-text')
              .attr('text-anchor', 'middle')
              .attr('dy', '-0.5em')
              .attr('fill', '#333')
              .attr('font-size', '12px')
              .attr('font-weight', 'bold')
              .text(d.data[this.labelKey]);

            const total = d3.sum(this.data, (item) => item[this.valueKey]);
            const percentage = total > 0 ? formatNum(((d.data[this.valueKey] / total) * 100), 1) : 0;

            svg.append('text')
              .attr('class', 'donut-hover-text')
              .attr('text-anchor', 'middle')
              .attr('dy', '1em')
              .attr('fill', '#666')
              .attr('font-size', '10px')
              .text(`${percentage}%`);
          }
        })
        .on('mouseout', (event) => {
          d3.select(event.currentTarget)
            .transition()
            .duration(200)
            .attr('d', arc);

          svg.selectAll('.donut-hover-text').remove();
        });

      if (!this.showLegend) {
        this.chart = svg;
        return;
      }

      // hindari .legend global
      const legend = svg.selectAll('.donut-legend')
        .data(this.data)
        .enter()
        .append('g')
        .attr('class', 'donut-legend')
        .attr('transform', (d, i) => `translate(-${this.width / 2 - 5},${radius + 5 + i * 18})`);

      legend.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', (d) => color(d[this.labelKey]));

      const total = d3.sum(this.data, (item) => item[this.valueKey]);
      legend.append('text')
        .attr('x', 18)
        .attr('y', 10)
        .attr('font-size', '10px')
        .attr('fill', '#666')
        .text((d) => {
          if (!this.valueFormatter) return d[this.labelKey];
          const pct = total > 0 ? formatNum((d[this.valueKey] / total) * 100, 1) : 0;
          return `${d[this.labelKey]} · ${this.valueFormatter(d[this.valueKey])} (${pct}%)`;
        });

      this.chart = svg;
    },
    destroyChart() {
      if (this.$refs.chartContainer) {
        d3.select(this.$refs.chartContainer).selectAll('*').remove();
      }
      this.chart = null;
    },
  },
};
</script>

<style scoped>
.donut-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
}

.donut-chart-container.donut-chart-mini {
  min-height: 0;
}

.donut-chart-container svg {
  max-width: 100%;
  height: auto;
  overflow: visible;
}

.donut-chart-container .arc path {
  transition: opacity 0.2s ease;
}

.donut-chart-container .arc path:hover {
  opacity: 0.8;
}

.donut-chart-container .donut-legend text {
  font-family: inherit;
}

.no-data {
  color: #6c757d;
  font-size: 0.875rem;
  text-align: center;
}
</style>
